/**
 * The club selector — FR-AU-04: clubs grouped by portal, the role shown
 * against each, plus the other hats an account wears: portals they own and the
 * platform console for the Super Admin.
 *
 * Restyled 26 Aug 2026 to the public home page's language (owner's direction);
 * the rules are written down at the top of `components/ui.jsx`. The
 * behaviour is untouched — same groups, same access states, same destinations.
 * What moved:
 *
 *   - The hero is the home page's ink gradient with its drifting glows, so the
 *     first screen after sign-in is visibly the same product as the first
 *     screen before it. It carries the real logo mark, not a text wordmark.
 *   - The doors are a hairline-divided grid rather than a stack of floating
 *     rounded cards: with three or four choices, four separate shadows read as
 *     four unrelated things, and this is ONE question with N answers.
 *   - Zero radius, Archivo throughout, gold for the platform accent and teal
 *     for the section eyebrows.
 *
 * Suspended and blocked portals stay LISTED, greyed, with the contact-your-
 * admin line (SRS §8.1) — hiding a membership reads as data loss.
 */

import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Icon, Pill, Reveal, RunnerCrewMark } from '../components/ui';

const ROLE_TONE = { admin: 'soon', coach: 'pend', member: 'grey' };
const ROLE_LABEL = { admin: 'Committee', coach: 'Coach', member: 'Member' };

function AccessNote({ state }) {
  if (state === 'suspended') {
    return (
      <p className="mt-1.5 font-market text-[11.5px] leading-snug text-flag-600">
        Temporarily unavailable — contact your club admin.
      </p>
    );
  }
  if (state === 'grace') {
    return (
      <p className="mt-1.5 font-market text-[11.5px] leading-snug text-gold-800">
        Subscription expired — read-only until renewed.
      </p>
    );
  }
  if (state === 'blocked') {
    return (
      <p className="mt-1.5 font-market text-[11.5px] leading-snug text-flag-600">
        Subscription expired — contact your club admin.
      </p>
    );
  }
  return null;
}

/** Teal eyebrow with an optional count hard right — the home page's opener. */
function SectionLabel({ children, right }) {
  return (
    <div className="mb-3 flex items-baseline justify-between gap-3">
      <h2 className="font-market text-[11px] font-bold uppercase tracking-[0.28em] text-sea-600">
        {children}
      </h2>
      {right != null && (
        <span className="font-market text-[11.5px] font-semibold tabular-nums text-copy-label">
          {right}
        </span>
      )}
    </div>
  );
}

/** The ink monogram square that fronts a portal row. */
function Monogram({ name }) {
  const letters = name
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <span className="grid h-12 w-12 flex-none place-items-center bg-ink-800 font-market text-[16px] font-extrabold text-gold-500">
      {letters}
    </span>
  );
}

/** The mini bib that fronts a club row — the member's real number. */
function MiniBib({ bib, dead }) {
  return (
    <span
      className={`relative grid h-12 w-14 flex-none place-items-center overflow-hidden font-market text-[17px] font-extrabold tabular-nums ${
        dead ? 'bg-line-chip text-copy-label' : 'bg-ink-800 text-white'
      }`}
    >
      <span
        className={`absolute inset-x-0 top-0 h-[3px] ${dead ? 'bg-line-strong' : 'bg-gold-500'}`}
      />
      {bib != null ? String(bib).padStart(3, '0') : '—'}
    </span>
  );
}

/** One door. The chevron is the affordance; the whole cell is the target. */
function DoorRow({ children, onClick, disabled, dead }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`group flex h-full w-full items-center gap-4 bg-white px-4 py-4 text-left transition-colors duration-300 ${
        dead ? 'cursor-not-allowed opacity-60' : 'hover:bg-ground-warm'
      }`}
    >
      {children}
    </button>
  );
}

export function SelectClubPage() {
  const { user, memberships, ownedPortals, isSuperAdmin, selectClub, signOut } = useAuth();
  const navigate = useNavigate();

  /* The entrance animation is a WELCOME, not a page style. This screen is
     also reached from "Switch club" in both shells, and replaying the hero
     rise and the card stagger every time you come back to pick a different
     door turns a flourish into a wait (owner's direction, 26 Aug 2026). Sign-in
     is the one arrival that sets this flag; every other route in lacks it. */
  const justSignedIn = useLocation().state?.fromSignIn === true;
  const intro = (cls) => (justSignedIn ? cls : '');

  const byPortal = useMemo(() => {
    const groups = new Map();
    for (const m of memberships) {
      const g = groups.get(m.portalId) ?? { portalName: m.portalName, clubs: [] };
      g.clubs.push(m);
      groups.set(m.portalId, g);
    }
    return [...groups.entries()];
  }, [memberships]);

  const firstName = user?.name?.split(' ')[0] ?? 'runner';
  const doorCount = memberships.length + ownedPortals.length + (isSuperAdmin ? 1 : 0);

  function openClub(m) {
    if (m.accessState === 'suspended' || m.accessState === 'blocked') return;
    selectClub(m.clubId);
    navigate('/');
  }

  return (
    <div className="min-h-screen bg-ground">
      {/* ---------- the ink hero ---------- */}
      <header className="relative overflow-hidden bg-[linear-gradient(168deg,#161C2C_0%,#0D1220_46%,#0A0E19_100%)]">
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute -right-40 -top-56 h-[620px] w-[620px] rounded-full bg-[radial-gradient(circle,rgba(246,192,25,.16),transparent_65%)] ${intro('motion-safe:animate-drift')}`}
        />
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute -bottom-56 -left-40 h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle,rgba(14,124,112,.26),transparent_65%)] ${intro('motion-safe:animate-drift-slow')}`}
        />

        <div className="relative mx-auto flex max-w-3xl items-center justify-between px-5 pt-6">
          <div className="flex items-center gap-2.5">
            <RunnerCrewMark size={30} />
            <span className="font-market text-[15.5px] font-extrabold tracking-[-0.02em] text-white">
              RunnerCrew
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              signOut();
              navigate('/signin');
            }}
            className="border border-white/[.18] bg-white/[.05] px-3 py-[7px] font-market text-[10px] font-bold uppercase tracking-[0.18em] text-white/60 transition-colors duration-200 hover:border-gold-500/60 hover:bg-gold-500/[.08] hover:text-gold-500"
          >
            Sign out
          </button>
        </div>

        <div className="relative mx-auto max-w-3xl px-5 pb-11 pt-9">
          <p className={`font-market text-[11px] font-bold uppercase tracking-[0.24em] text-gold-500 ${intro('motion-safe:animate-fadein')}`}>
            One account · every club
          </p>
          <h1 className={`mt-4 font-market text-[34px] font-extrabold leading-[1.04] tracking-[-0.035em] text-white sm:text-[42px] ${intro('motion-safe:animate-rise-in')}`}>
            Where to,{' '}
            <span className="bg-[linear-gradient(100deg,#FFE383,#F6C019_46%,#7FD8C8)] bg-clip-text text-transparent">
              {firstName}
            </span>
            ?
          </h1>
          <p className={`mt-3.5 max-w-md font-market text-[15.5px] leading-[1.7] text-white/[.62] ${intro('motion-safe:animate-rise-in [animation-delay:120ms]')}`}>
            {doorCount === 1
              ? 'One door today. Step through.'
              : `${doorCount} doors are open for you. Pick where you are headed.`}
          </p>
        </div>
        <div className="h-[3px] bg-[linear-gradient(90deg,#F6C019,#7FD8C8)]" />
      </header>

      <main className="mx-auto max-w-3xl px-5 pb-16 pt-9">
        {/* ---------- the platform console ---------- */}
        {isSuperAdmin && (
          <Reveal index={0} enabled={justSignedIn}>
            <button
              type="button"
              onClick={() => navigate('/sa/dashboard')}
              className="group mb-9 flex w-full items-center gap-4 border border-ink-700 bg-[linear-gradient(120deg,#161C2C,#0B0F18)] p-5 text-left text-white transition-colors duration-300 hover:border-gold-500/50"
            >
              <span className="grid h-12 w-12 flex-none place-items-center bg-gold-500 text-ink-800">
                <Icon name="grid" size={20} strokeWidth={2} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-market text-[9.5px] font-bold uppercase tracking-[0.24em] text-gold-500">
                  Platform owner
                </span>
                <span className="mt-1 block font-market text-[19px] font-extrabold tracking-[-0.025em] leading-tight">
                  Super Admin console
                </span>
                <span className="mt-1 block truncate font-market text-[12.5px] text-white/50">
                  Portal requests · plans · payments · trace log
                </span>
              </span>
              <span className="font-market text-2xl text-gold-500 transition-transform duration-200 group-hover:translate-x-1">
                →
              </span>
            </button>
          </Reveal>
        )}

        {/* ---------- portals you own ---------- */}
        {ownedPortals.length > 0 && (
          <section className="mb-9">
            <SectionLabel
              right={`${ownedPortals.length} portal${ownedPortals.length === 1 ? '' : 's'}`}
            >
              You run the show
            </SectionLabel>
            <div className="grid gap-px border border-line bg-line">
              {ownedPortals.map((p, i) => (
                <Reveal key={p.portalId} index={i + 1} enabled={justSignedIn}>
                  <DoorRow onClick={() => navigate(`/p/${p.portalId}`)}>
                    <Monogram name={p.portalName} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-market text-[16.5px] font-extrabold tracking-[-0.02em] leading-tight text-ink-700">
                        {p.portalName}
                      </span>
                      <span className="mt-1 block font-market text-[11.5px] text-copy-muted">
                        Seats · clubs · subscription
                      </span>
                      <AccessNote state={p.accessState} />
                    </span>
                    <Pill tone="soon">Owner</Pill>
                    <span className="hidden font-market text-xl text-line-strong transition-all duration-200 group-hover:translate-x-1 group-hover:text-sea-600 sm:block">
                      →
                    </span>
                  </DoorRow>
                </Reveal>
              ))}
            </div>
          </section>
        )}

        {/* ---------- clubs, grouped by the portal that runs them ---------- */}
        {byPortal.map(([portalId, group], gi) => (
          <section key={portalId} className="mb-9">
            <SectionLabel
              right={`${group.clubs.length} club${group.clubs.length === 1 ? '' : 's'}`}
            >
              {group.portalName}
            </SectionLabel>
            <div
              className={`grid gap-px border border-line bg-line ${
                group.clubs.length > 1 ? 'sm:grid-cols-2' : ''
              }`}
            >
              {group.clubs.map((m, i) => {
                const dead = m.accessState === 'suspended' || m.accessState === 'blocked';
                return (
                  <Reveal key={m.membershipId} index={gi + i + 1} enabled={justSignedIn}>
                    <DoorRow onClick={() => openClub(m)} disabled={dead} dead={dead}>
                      <MiniBib bib={m.bib} dead={dead} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-market text-[16.5px] font-extrabold tracking-[-0.02em] leading-tight text-ink-700">
                          {m.clubName}
                        </span>
                        <span className="mt-1 block font-market text-[11.5px] tabular-nums text-copy-muted">
                          {m.bib != null ? `Bib ${String(m.bib).padStart(3, '0')}` : 'No bib yet'}
                        </span>
                        <AccessNote state={m.accessState} />
                      </span>
                      <Pill tone={ROLE_TONE[m.role] ?? 'grey'}>
                        {ROLE_LABEL[m.role] ?? m.role}
                      </Pill>
                    </DoorRow>
                  </Reveal>
                );
              })}
            </div>
          </section>
        ))}

        {/* ---------- nobody's runner yet ---------- */}
        {memberships.length === 0 && ownedPortals.length === 0 && !isSuperAdmin && (
          <div className="border border-line bg-white p-9 text-center">
            <p className="font-market text-[22px] font-extrabold tracking-[-0.03em] text-ink-700">
              No clubs yet
            </p>
            <p className="mx-auto mt-2.5 max-w-sm font-market text-[14.5px] leading-[1.65] text-copy-muted">
              Your account exists, but no club lists you as a member. Ask your club admin to add
              you — or start your own organisation.
            </p>
            <button
              type="button"
              onClick={() => navigate('/register-portal')}
              className="mt-6 bg-[linear-gradient(140deg,#FFD75E,#F6C019_60%,#EFB000)] px-6 py-3.5 font-market text-[14px] font-extrabold text-ink-800 shadow-[0_12px_26px_-14px_rgba(246,192,25,.9)] transition-all duration-200 hover:-translate-y-px hover:brightness-105"
            >
              Create an admin portal
            </button>
          </div>
        )}

        <p className="mt-11 text-center font-market text-[11px] font-semibold uppercase tracking-[0.16em] text-copy-label">
          Signed in as {user?.email} · switch clubs anytime from the menu
        </p>
      </main>
    </div>
  );
}
